/**
 * Apps API Response Mappers
 *
 * Each function handles a specific API endpoint response format.
 */
import { reverseMap, transformArray, transformKeys } from '@/shared/mappers/base.mapper';

import type {
  Application,
  ApplicationIntegrationCount,
  RecommendedAction,
  ResourceType,
  UserPermission,
} from '@/types';

// =============================================================================
// Field Mappings (Backend → Frontend)
// =============================================================================

/**
 * Application field mapping
 */
const APPLICATION_FIELDS = {
  Id: 'id',
  Name: 'name',
  Description: 'description',
  UniqueKey: 'uniqueKey',
  IntegrationCount: 'integrationCount',
  TenantName: 'tenantName',
  Archived: 'archived',
  CreatedAt: 'createdAt',
  UpdatedAt: 'updatedAt',
  // Backend sends lowercase, normalize to camelCase
  tenantid: 'tenantId',
  uniquekey: 'uniqueKey',
};

/**
 * ApplicationIntegrationCount field mapping
 */
const APPLICATION_INTEGRATION_COUNT_FIELDS = {
  AppId: 'appId',
  Name: 'name',
  Count: 'count',
};

/**
 * ResourceType field mapping
 */
const RESOURCE_TYPE_FIELDS = {
  Name: 'name',
  Description: 'description',
  Fields: 'fields',
  Category: 'category',
};

/**
 * RecommendedAction field mapping
 */
const RECOMMENDED_ACTION_FIELDS = {
  Id: 'id',
  Name: 'name',
  Description: 'description',
  AccessLevel: 'accessLevel',
  Category: 'category',
};

/**
 * UserPermission field mapping
 */
const USER_PERMISSION_FIELDS = {
  UserId: 'userId',
  IdentityId: 'userId', // Backend sends IdentityId, map to userId
  Username: 'username',
  Email: 'email',
  IsAdmin: 'isAdmin',
  TenantId: 'tenantId',
  AppId: 'appId',
  Identity: 'identity',
  // Preserve camelCase: userId, username, email, isAdmin, tenantId, appId
};

// =============================================================================
// Response Transformers (Backend → Frontend)
// =============================================================================

/**
 * Transform GET /applications response
 * Returns array of applications for a tenant
 */
export const transformGetApplicationsResponse = (response: unknown): Application[] => {
  return transformArray(response, (item) => {
    const transformed = transformKeys<Application>(item, APPLICATION_FIELDS, {
      parseJson: false,
      recursive: true,
    });

    // Ensure id and tenantId are always strings
    if (transformed.id !== undefined) {
      transformed.id = String(transformed.id);
    }
    if (transformed.tenantId !== undefined) {
      transformed.tenantId = String(transformed.tenantId);
    }

    return transformed;
  });
};

/**
 * Transform GET /application/{id} response
 * Returns single application details
 */
export const transformGetApplicationResponse = (response: unknown): Application => {
  const transformed = transformKeys<Application>(response, APPLICATION_FIELDS, {
    parseJson: false,
    recursive: true,
  });

  // Ensure id and tenantId are always strings
  if (transformed.id !== undefined) {
    transformed.id = String(transformed.id);
  }
  if (transformed.tenantId !== undefined) {
    transformed.tenantId = String(transformed.tenantId);
  }

  return transformed;
};

/**
 * Transform GET /application-integration-counts response
 * Returns array of apps with integration counts
 */
export const transformGetApplicationIntegrationCountsResponse = (
  response: unknown
): ApplicationIntegrationCount[] => {
  return transformArray(response, (item) =>
    transformKeys<ApplicationIntegrationCount>(item, APPLICATION_INTEGRATION_COUNT_FIELDS, {
      parseJson: false,
      recursive: true,
    })
  );
};

/**
 * Transform POST /application response
 * Returns newly created application
 */
export const transformCreateApplicationResponse = (response: unknown): Application => {
  const transformed = transformKeys<Application>(response, APPLICATION_FIELDS, {
    parseJson: false,
    recursive: true,
  });

  // Ensure id and tenantId are always strings
  if (transformed.id !== undefined) {
    transformed.id = String(transformed.id);
  }
  if (transformed.tenantId !== undefined) {
    transformed.tenantId = String(transformed.tenantId);
  }

  return transformed;
};

/**
 * Transform PUT /application response
 * Returns updated application
 */
export const transformUpdateApplicationResponse = (response: unknown): Application => {
  const transformed = transformKeys<Application>(response, APPLICATION_FIELDS, {
    parseJson: false,
    recursive: true,
  });

  // Ensure id and tenantId are always strings
  if (transformed.id !== undefined) {
    transformed.id = String(transformed.id);
  }
  if (transformed.tenantId !== undefined) {
    transformed.tenantId = String(transformed.tenantId);
  }

  return transformed;
};

/**
 * Transform GET /resource-types response
 * Returns array of resource types
 */
export const transformGetResourceTypesResponse = (response: unknown): ResourceType[] => {
  return transformArray(response, (item) =>
    transformKeys<ResourceType>(item, RESOURCE_TYPE_FIELDS, {
      parseJson: false,
      recursive: true,
    })
  );
};

/**
 * Transform GET /recommended-actions response
 * Returns array of recommended actions
 */
export const transformGetRecommendedActionsResponse = (response: unknown): RecommendedAction[] => {
  return transformArray(response, (item) =>
    transformKeys<RecommendedAction>(item, RECOMMENDED_ACTION_FIELDS, {
      parseJson: false,
      recursive: true,
    })
  );
};

/**
 * Transform GET /workspace-users response
 * Returns array of users with permissions
 *
 * API Response structure (from dataProtect):
 * [{
 *   UserId: number,
 *   IsAdmin: boolean,
 *   Identity: {
 *     Id: number,
 *     Username: string (email)
 *   }
 * }]
 *
 * Filters out users without valid Identity (matches dataProtect userPicker.js line 74)
 */
export const transformGetWorkspaceUsersResponse = (response: unknown): UserPermission[] => {
  type WorkspaceUserItem = {
    UserId?: number;
    userId?: number;
    IsAdmin?: boolean;
    isAdmin?: boolean;
    Identity?: { Id?: number; Username?: string };
    identity?: { id?: number; username?: string };
    Username?: string;
    username?: string;
    Email?: string;
    email?: string;
  };
  return transformArray(response, (item: WorkspaceUserItem) => {
    const identity = item.Identity || item.identity;
    const userId = item.UserId ?? item.userId ?? identity?.Id ?? identity?.id;
    const username = identity?.Username ?? identity?.username ?? item.Username ?? item.username;
    const email = identity?.Username ?? identity?.username ?? item.Email ?? item.email;

    // Filter out invalid users (matches legacy dataProtect behavior)
    // Skip users without valid identity (no userId or no username/email)
    if (!userId || userId <= 0) return null;
    if (!username && !email) return null;

    return {
      userId,
      username,
      email,
      isAdmin: item.IsAdmin ?? item.isAdmin ?? false,
    };
  }).filter((user): user is UserPermission => user !== null);
};

// =============================================================================
// Request Transformers (Frontend → Backend)
// =============================================================================

/**
 * Transform POST /application request body
 */
export const transformCreateApplicationRequest = (data: Partial<Application>): unknown => {
  const reverseFields = reverseMap(APPLICATION_FIELDS);
  return transformKeys(data, reverseFields, {
    parseJson: false,
    recursive: true,
  });
};

/**
 * Transform PUT /application request body
 */
export const transformUpdateApplicationRequest = (data: Partial<Application>): unknown => {
  const reverseFields = reverseMap(APPLICATION_FIELDS);
  return transformKeys(data, reverseFields, {
    parseJson: false,
    recursive: true,
  });
};

/**
 * Transform POST /invite-workspace-users request body
 */
export const transformInviteWorkspaceUsersRequest = (data: unknown): unknown => {
  const reverseFields = reverseMap(USER_PERMISSION_FIELDS);
  return transformKeys(data, reverseFields, {
    parseJson: false,
    recursive: true,
  });
};
